import { Badge, Box, Text, cn, type BadgeProps } from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { StageIcon, type StageIconProps } from "#app/components/entity/stage-icon";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  UNASSIGNED_DIMENSION_LABEL,
  UNASSIGNED_GROUP_KEY,
  type IssueGroupDimension,
} from "./issue-grouping";

export type StageGroupChipProps = Omit<
  BadgeProps,
  "color" | "tone" | "size" | "children"
> & {
  stage: NonNullable<StageIconProps["stage"]> & { name: string };
  /**
   * `horizontal` (default) is the 24px-tall band chip. `vertical` is the same
   * chip stood on end — 24px wide, pie on top, the name running down in
   * `vertical-rl` — for a collapsed board column's 40px rail, so the housing
   * folds with the column instead of falling away.
   */
  orientation?: "horizontal" | "vertical";
};

/**
 * A stage group's band label: the stage as the library's NEUTRAL chip — the
 * `secondary` fill every stage shares, with the name on the secondary ink —
 * and the pie alone in the stage's hue. The band wears a HOUSED stage while
 * the rows beneath it wear the bare pie (the name cell's change-stage
 * control), so the two read as heading and item even though they share one
 * glyph and one vertical column. The housing is grey on purpose: a hue-tinted
 * chip repeats the pie's hue as a fill, and a stack of groups then reads as a
 * wall of color before it reads as headings. Hue appears once per band, in
 * the glyph, exactly as it does in the rows. `secondary` rather than the
 * colorless chip because the name is the band's HEADING: colorless reads its
 * ink off `--muted-foreground`, the rung the count beside it sits on, and a
 * heading on its own meta ink is a caption. Badge props pass through, so the
 * chip can render as the rename trigger (`render={<button />}`).
 */
export function StageGroupChip({
  stage,
  orientation = "horizontal",
  className,
  ...props
}: StageGroupChipProps) {
  return (
    <Badge
      color="secondary"
      tone="tonal"
      size="chip"
      className={cn(
        orientation === "vertical"
          ? // Stood on end: the writing mode turns the flex row's main axis
            // vertical (pie, gap, name top-to-bottom) and rotates the label
            // the way the rail's own vertical text runs. The chip's 7px
            // inset is padding-INLINE, so it follows the writing mode to the
            // top and bottom ends on its own; only the fixed 24px is physical
            // and swaps axes here — width 24, height from content.
            "[writing-mode:vertical-rl] h-auto w-6 min-h-6"
          : // The band title lands on the column content edge; the rows' pie
            // sits 4px inside that edge (its 24px ghost action box) while the
            // chip insets its pie 8px (7px padding + the Badge's 1px border).
            // -4px puts the header pie on the row pies' axis, so the glyph
            // column stays one column and only the housing differs.
            "-ml-1",
        className,
      )}
      {...props}
    >
      <StageIcon stage={stage} size="sm" />
      <Text size="xs" weight="medium" truncate>
        {stage.name}
      </Text>
    </Badge>
  );
}

/**
 * Group-band title for an issue table grouped by an entity dimension: the
 * group's entity chip (stage/workflow/type resolved from workspace data), or
 * the dimension's muted "no value" label for the unassigned bucket. Dimensions
 * whose group labels are plain text (age) don't mount it — the table's own
 * label rendering covers those.
 */
export function IssueGroupTitle({
  dimension,
  groupKey,
}: {
  dimension: IssueGroupDimension;
  groupKey: string;
}) {
  const { stageMap, workflowMap, issueTypeMap } = useWorkspaceData();

  if (groupKey === UNASSIGNED_GROUP_KEY) {
    return (
      <Text size="sm" variant="muted" weight="medium">
        {UNASSIGNED_DIMENSION_LABEL[dimension]}
      </Text>
    );
  }

  if (dimension === "stage") {
    const stage = stageMap.get(groupKey);
    if (!stage) return groupKey;
    // min-w-0, not overflow-hidden: the chip pulls itself 4px left onto the
    // row pies' axis, and a clipping wrapper would shear that edge off. The
    // flex row still shrinks, so the name truncates.
    return (
      <Box direction="row" align="center" gap="sm" minWidth={0}>
        <StageGroupChip stage={stage} />
      </Box>
    );
  }

  if (dimension === "workflow") {
    const workflow = workflowMap.get(groupKey);
    if (!workflow) return groupKey;
    return (
      <Box direction="row" align="center" gap="sm" overflow="hidden">
        <EntityTag
          entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
          interactive={false}
        />
      </Box>
    );
  }

  if (dimension === "issueType") {
    const issueType = issueTypeMap.get(groupKey);
    if (!issueType) return groupKey;
    return (
      <Box direction="row" align="center" gap="sm" overflow="hidden">
        <EntityTag
          entity={{ kind: "issueType", id: issueType.issueTypeId, issueType }}
          size="md"
          interactive={false}
        />
      </Box>
    );
  }

  return groupKey;
}
